import { useState } from "react";
import { useNavigate } from "react-router-dom";
import posthog from "posthog-js";
import { Star, Send, Camera } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateCafeData, invalidateUserStats } from "@/lib/queryKeys";
import { cn } from "@/lib/utils";
import { PhotoUpload } from "@/components/PhotoUpload";
import { useBadgeCheck } from "@/hooks/useBadges";
import { Mascot } from "@/components/Mascot";
import { useFeedbackTracker } from "@/components/FeedbackPromptProvider";

interface EditReviewData {
  id: string;
  rating: number;
  work_friendly_rating: number | null;
  title: string | null;
  content: string | null;
  would_work_again: boolean | null;
}

interface WriteReviewDialogProps {
  cafeId: string;
  cafeName: string;
  trigger?: React.ReactNode;
  /** Controlled mode - if provided, component is controlled externally */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** If true, shows context-aware copy for post-check-in flow */
  afterCheckIn?: boolean;
  /** Optional check-in ID to link this review to a check-in */
  checkInId?: string;
  /** If provided, dialog opens in edit mode with pre-filled data */
  editReview?: EditReviewData;
}

export function WriteReviewDialog({ 
  cafeId, 
  cafeName, 
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  afterCheckIn = false,
  checkInId,
  editReview,
}: WriteReviewDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { user } = useAuth();
  const [rating, setRating] = useState(editReview?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [workRating, setWorkRating] = useState(editReview?.work_friendly_rating || 0);
  const [hoverWorkRating, setHoverWorkRating] = useState(0);
  const [title] = useState(editReview?.title || "");
  const [content, setContent] = useState(editReview?.content || "");
  const [wouldWorkAgain, setWouldWorkAgain] = useState(editReview?.would_work_again ?? true);
  const isEditMode = !!editReview;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { checkBadges } = useBadgeCheck();
  const { trackAction } = useFeedbackTracker();

  // Determine if controlled or uncontrolled
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange || (() => {})) : setInternalOpen;


  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && !user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to write a review.",
        action: <ToastAction altText="Sign In" onClick={() => navigate("/auth", { state: { from: window.location.pathname } })}>Sign In</ToastAction>,
      });
      return;
    }
    setOpen(newOpen);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: "Rating required",
        description: "Please select a rating before submitting.",
        variant: "destructive",
      });
      return;
    }

    if (content.trim().length < 10) {
      toast({
        title: "Review too short",
        description: "Please write at least 10 characters.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const reviewData = {
        rating,
        work_friendly_rating: workRating || null,
        title: title.trim() || null,
        content: content.trim(),
        would_work_again: wouldWorkAgain,
        check_in_id: checkInId || null,
      };

      let isUpdate = false;

      if (isEditMode && editReview) {
        // Direct update using the known review ID
        const { error } = await supabase
          .from("reviews")
          .update(reviewData)
          .eq("id", editReview.id);
        if (error) throw error;
        isUpdate = true;
      } else {
        // Check for existing review by this user for this cafe
        const { data: existing } = await supabase
          .from("reviews")
          .select("id")
          .eq("cafe_id", cafeId)
          .eq("user_id", user.id)
          .maybeSingle();

        if (existing) {
          const { error } = await supabase
            .from("reviews")
            .update(reviewData)
            .eq("id", existing.id);
          if (error) throw error;
          isUpdate = true;
        } else {
          const { error } = await supabase.from("reviews").insert({
            cafe_id: cafeId,
            user_id: user.id,
            ...reviewData,
          });
          if (error) throw error;
        }
      }

      try {
        posthog.capture("review_submitted", {
          cafe_id: cafeId,
          rating,
          has_text: content.trim().length > 0,
          is_edit: isUpdate,
        });
      } catch { /* ignore */ }


      toast({
        title: (
          <div className="flex items-center gap-2">
            <Mascot mood="happy" size="xs" />
            <span>{isUpdate ? "Review updated!" : "Review submitted!"}</span>
          </div>
        ) as unknown as string,
        description: isUpdate
          ? "Your review has been updated."
          : "Thanks for sharing your experience.",
      });

      // Check for badges (async, don't await to not block UI)
      checkBadges(user.id, 'review', cafeId);

      // Track action for feedback prompt
      trackAction();

      // Reset form and close
      setRating(0);
      setWorkRating(0);
      
      setContent("");
      setWouldWorkAgain(true);
      setOpen(false);
      
      // Refresh reviews + all derived views (cafe stats trigger, feed_items trigger, profile stats)
      invalidateCafeData(queryClient, cafeId);
      queryClient.invalidateQueries({ queryKey: ["reviews", cafeId] });
      queryClient.invalidateQueries({ queryKey: ["feed"] });
      if (user?.id) invalidateUserStats(queryClient, user.id);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to submit review.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const StarRating = ({ 
    value, 
    hoverValue, 
    onChange, 
    onHover, 
    onLeave,
    label 
  }: { 
    value: number; 
    hoverValue: number;
    onChange: (v: number) => void; 
    onHover: (v: number) => void;
    onLeave: () => void;
    label: string;
  }) => (
    <div className="space-y-1.5">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-1" onMouseLeave={onLeave}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => onHover(star)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            <Star
              className={cn(
                "h-6 w-6 transition-colors",
                star <= (hoverValue || value)
                  ? "fill-caramel text-caramel"
                  : "text-muted-foreground/30"
              )}
            />
          </button>
        ))}
      </div>
    </div>
  );

  // In controlled mode without trigger, render just the dialog content
  if (isControlled && !trigger) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {isEditMode ? `Edit Review for ${cafeName}` : afterCheckIn ? "Tell us more about your visit" : `Review ${cafeName}`}
            </DialogTitle>
            {afterCheckIn && (
              <DialogDescription>
                Share your overall experience to help others decide
              </DialogDescription>
            )}
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <StarRating
              value={rating}
              hoverValue={hoverRating}
              onChange={setRating}
              onHover={setHoverRating}
              onLeave={() => setHoverRating(0)}
              label="Overall Rating *"
            />

            {!afterCheckIn && (
              <>
                <StarRating
                  value={workRating}
                  hoverValue={hoverWorkRating}
                  onChange={setWorkRating}
                  onHover={setHoverWorkRating}
                  onLeave={() => setHoverWorkRating(0)}
                  label="Work-Friendly Rating (optional)"
                />

                <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{wouldWorkAgain ? "👍" : "👎"}</span>
                    <div>
                      <Label className="font-medium">Would work here again?</Label>
                      <p className="text-xs text-muted-foreground">Overall recommendation</p>
                    </div>
                  </div>
                  <Switch checked={wouldWorkAgain} onCheckedChange={setWouldWorkAgain} />
                </div>
              </>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="content" className="text-sm">Your Review *</Label>
              <Textarea
                id="content"
                placeholder="Share your experience working here..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground text-right">
                {content.length}/1000
              </p>
            </div>

            {/* Photo Upload - hidden after check-in since photos were already captured */}
            {!afterCheckIn && (
              <div className="space-y-1.5 pt-2 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm">Add a photo (optional)</Label>
                </div>
                <PhotoUpload cafeId={cafeId} cafeName={cafeName} compact />
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full btn-cozy" 
              disabled={loading || rating === 0}
            >
              {loading ? (isEditMode ? "Updating..." : "Submitting...") : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {isEditMode ? "Update Review" : "Submit Review"}
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            Write Review
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEditMode ? `Edit Review for ${cafeName}` : afterCheckIn ? "Tell us more about your visit" : `Review ${cafeName}`}
          </DialogTitle>
          {afterCheckIn && (
            <DialogDescription>
              Share your overall experience to help others decide
            </DialogDescription>
          )}
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <StarRating
            value={rating}
            hoverValue={hoverRating}
            onChange={setRating}
            onHover={setHoverRating}
            onLeave={() => setHoverRating(0)}
            label="Overall Rating *"
          />

          {!afterCheckIn && (
            <>
              <StarRating
                value={workRating}
                hoverValue={hoverWorkRating}
                onChange={setWorkRating}
                onHover={setHoverWorkRating}
                onLeave={() => setHoverWorkRating(0)}
                label="Work-Friendly Rating (optional)"
              />

              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{wouldWorkAgain ? "👍" : "👎"}</span>
                  <div>
                    <Label className="font-medium">Would work here again?</Label>
                    <p className="text-xs text-muted-foreground">Overall recommendation</p>
                  </div>
                </div>
                <Switch checked={wouldWorkAgain} onCheckedChange={setWouldWorkAgain} />
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="content" className="text-sm">Your Review *</Label>
            <Textarea
              id="content"
              placeholder="Share your experience working here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {content.length}/1000
            </p>
          </div>

          {/* Photo Upload - hidden after check-in since photos were already captured */}
          {!afterCheckIn && (
            <div className="space-y-1.5 pt-2 border-t border-border/50">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm">Add a photo (optional)</Label>
              </div>
              <PhotoUpload cafeId={cafeId} cafeName={cafeName} compact />
            </div>
          )}

          <Button 
            type="submit" 
            className="w-full btn-cozy" 
            disabled={loading || rating === 0}
          >
            {loading ? (isEditMode ? "Updating..." : "Submitting...") : (
              <>
                <Send className="h-4 w-4 mr-2" />
                {isEditMode ? "Update Review" : "Submit Review"}
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
